import { Modal, Field, cx } from "./UI";
import { ExternalLink, FileText, Ruler } from "lucide-react";

// ─── File type helpers ─────────────────────────────────────────────────────

export const getFileIcon = (type) => {
  const icons = { pdf: "📄", dwg: "📐", dxf: "📏" };
  return icons[type?.toLowerCase()] || "📁";
};

export const getFileBadgeStyle = (type) => {
  const styles = {
    pdf: "bg-red-50 text-red-600 border-red-200",
    dwg: "bg-brand-50 text-brand-600 border-brand-200",
    dxf: "bg-cyan-50 text-cyan-600 border-cyan-200",
  };
  return styles[type?.toLowerCase()] || "bg-ink-100 text-ink-600 border-ink-200";
};

export const formatBytes = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/* Shared chrome above every preview frame */
function ViewerBar({ icon, fileName, kind, fileUrl }) {
  return (
    <div className="bg-ink-50 border border-ink-200 rounded-t-xl px-3 py-2 flex items-center justify-between gap-3">
      <span className="text-xs text-ink-500 flex items-center gap-2 min-w-0">
        {icon}
        <span className="font-mono truncate">{fileName}</span>
        <span className={cx("text-[10px] px-1.5 py-0.5 rounded border uppercase shrink-0", getFileBadgeStyle(kind))}>{kind}</span>
      </span>
      <a href={fileUrl} target="_blank" rel="noopener noreferrer"
        className="text-xs font-semibold text-brand-600 hover:text-brand-700 flex items-center gap-1 shrink-0">
        <ExternalLink size={12} /> <span className="hidden sm:inline">Open in new tab</span>
      </a>
    </div>
  );
}

/* DWG / DXF preview.
   ShareCAD renders the file in an iframe; for full editing the file has to be
   downloaded. Swapping in Autodesk APS (Model Derivative → SVF2) would need
   server-side credentials. */
function CadViewer({ fileUrl, fileName }) {
  const viewerUrl = `https://sharecad.org/cadframe/load?url=${encodeURIComponent(fileUrl)}`;
  const kind = fileName?.split(".").pop() || "dwg";
  return (
    <div className="flex flex-col h-full">
      <ViewerBar icon={<Ruler size={13} />} fileName={fileName} kind={kind} fileUrl={fileUrl} />
      <div className="relative flex-1 bg-white rounded-b-xl overflow-hidden border border-t-0 border-ink-200">
        <iframe src={viewerUrl} title={`CAD Viewer – ${fileName}`} className="w-full h-full border-0" allow="fullscreen" />
      </div>
      <p className="text-[11px] text-ink-400 mt-2 text-center">Preview by ShareCAD · download the file for full DWG editing</p>
    </div>
  );
}

function PdfViewer({ fileUrl, fileName }) {
  return (
    <div className="flex flex-col h-full">
      <ViewerBar icon={<FileText size={13} />} fileName={fileName} kind="pdf" fileUrl={fileUrl} />
      <iframe
        src={`${fileUrl}#toolbar=1&navpanes=0&scrollbar=1`}
        title={`PDF – ${fileName}`}
        className="flex-1 w-full border border-t-0 border-ink-200 rounded-b-xl bg-white"
      />
    </div>
  );
}

export function DrawingViewerModal({ drawing, onClose }) {
  if (!drawing) return null;
  const isPdf = drawing.fileType?.toLowerCase() === "pdf";

  return (
    <Modal
      open={!!drawing}
      onClose={onClose}
      title={drawing.title}
      subtitle={`v${drawing.version} · ${drawing.drawingCode}`}
      icon={<Ruler size={18} />}
      size="xl"
    >
      {/* dvh keeps the frame usable on mobile browsers with a collapsing toolbar */}
      <div className="flex flex-col h-[58dvh] sm:h-[64vh]">
        {isPdf
          ? <PdfViewer fileUrl={drawing.firebaseURL} fileName={drawing.fileName} />
          : <CadViewer fileUrl={drawing.firebaseURL} fileName={drawing.fileName} />}
      </div>

      <div className="mt-4 pt-4 border-t border-ink-100 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Field label="Drawing Code" value={<span className="font-mono">{drawing.drawingCode}</span>} />
        <Field label="Version" value={`v${drawing.version}`} />
        <Field label="Uploaded by" value={drawing.uploadedByName} />
        <Field label="File size" value={formatBytes(drawing.fileSize)} />
      </div>
    </Modal>
  );
}
